import { Button, TableCell, TableRow, TextField, Typography } from "@mui/material"
import { StandardCombo } from "lib/@components/combos/standardCombo"
import { invalidate } from "lib/graphql/cache"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { COLLABORATE_LICENSE, CREATE_LICENSE } from "library/license-types.mjs"
import { If, Else } from "lib/@components/switch"
import { upsertScimGroupMapping } from "routes/settings/plugins/scim/controller/upsert-scim-group-mapping"
import { useState } from "react"
import { RolesPicker } from "./roles-picker"

// Mirror the Users-tab license dropdown: see lib/licenses/components/license-allocator.js.
const LICENSE_OPTIONS = ["none", CREATE_LICENSE, COLLABORATE_LICENSE]

const EMPTY_FORM = { groupId: "", externalId: "", displayName: "", license: "none", roleIds: [], priority: 0 }

function licenseLabel(value) {
    if (!value || value === "none") return "None"
    return value.charAt(0).toUpperCase() + value.slice(1)
}

export function NewMappingRow({ clientId, knownGroups, rolesAvailable }) {
    const [form, setForm] = useState(EMPTY_FORM)

    const groupOptions = knownGroups.map(toGroupOption)

    return (
        <TableRow sx={{ backgroundColor: "action.hover" }}>
            <TableCell sx={{ verticalAlign: "middle" }}>
                <StandardCombo
                    size="small"
                    options={[{ value: "", label: "(pick synced group)" }, ...groupOptions]}
                    value={form.groupId}
                    onChange={handleGroupChange}
                />
            </TableCell>
            <TableCell>
                <StandardCombo
                    size="small"
                    options={LICENSE_OPTIONS}
                    value={form.license}
                    onChange={handleLicenseChange}
                    getOptionLabel={licenseLabel}
                />
            </TableCell>
            <TableCell>
                <If value={form.license === CREATE_LICENSE}>
                    <RolesPicker roles={rolesAvailable} value={form.roleIds} onChange={handleRolesChange} />
                    <Else>
                        <Typography variant="body2" color="text.disabled" sx={{ px: 1 }}>
                            N/A
                        </Typography>
                    </Else>
                </If>
            </TableCell>
            <TableCell align="center">
                <TextField
                    size="small"
                    type="number"
                    value={form.priority}
                    onChange={handlePriorityChange}
                    sx={{ width: 70 }}
                />
            </TableCell>
            <TableCell align="right">
                <Button size="small" variant="contained" onClick={add} disabled={!form.groupId}>
                    Add
                </Button>
            </TableCell>
        </TableRow>
    )

    function handleLicenseChange(e) {
        const license = e.target.value
        setForm({ ...form, license, roleIds: license === CREATE_LICENSE ? form.roleIds : [] })
    }

    function handleRolesChange(roleIds) {
        setForm({ ...form, roleIds })
    }

    function handlePriorityChange(e) {
        setForm({ ...form, priority: Number(e.target.value) || 0 })
    }

    function handleGroupChange(e) {
        const { externalId = "", displayName = "" } = knownGroups.find((g) => g._id === e.target.value) ?? {}
        setForm({ ...form, groupId: e.target.value, externalId, displayName })
    }

    function toGroupOption(g) {
        return {
            value: g._id,
            label: `${g.displayName} · ${g.memberCount} member${g.memberCount === 1 ? "" : "s"}`,
        }
    }

    async function add() {
        if (!form.groupId) {
            errorSnackbar("Pick a synced group first")
            return
        }
        try {
            await upsertScimGroupMapping({
                clientId,
                input: {
                    displayName: form.displayName || null,
                    externalId: form.externalId || null,
                    license: form.license === "none" ? null : form.license,
                    roles: form.roleIds,
                    priority: form.priority,
                },
            })
            invalidate("getScimGroupMappings")
            successSnackbar("Mapping added — affected users reconciled")
            setForm(EMPTY_FORM)
        } catch (error) {
            errorSnackbar(error?.message || "Add failed")
        }
    }
}
